import React from "react";
import { useNavigate } from 'react-router-dom';

const LocationPageContent = ({ className, locationData }) => {
  const navigate = useNavigate();
  
  if (!locationData) return null;

  // Handle pickup button click
  const handlePickupClick = () => {
    
    if (locationData.pickupUrl) {
      window.open(locationData.pickupUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('❌ No pickup URL available for this location');
    }
  };

  // Handle delivery button click
  const handleDeliveryClick = () => {
    
    if (locationData.deliveryUrl) {
      window.open(locationData.deliveryUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('❌ No delivery URL available for this location');
    }
  };

  // Handle catering button click
  const handleCateringClick = () => {
    navigate('/catering');
  };

  // Format time from 24hr to 12hr with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Check if all days have the same hours
  const formatHours = (hours) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    // Check if all days have same hours and none are closed
    const firstDay = hours[days[0]];
    const allSame = days.every(day => 
      hours[day].open === firstDay.open && 
      hours[day].close === firstDay.close && 
      hours[day].closed === firstDay.closed
    );

    if (allSame && !firstDay.closed) {
      return (
        <div>Monday - Sunday {formatTime(firstDay.open)} - {formatTime(firstDay.close)}</div>
      );
    } else {
      return days.map((day, index) => {
        const dayHours = hours[day];
        if (dayHours.closed) {
          return <div key={day}>{dayNames[index]} Closed</div>;
        }
        return (
          <div key={day}>
            {dayNames[index]} {formatTime(dayHours.open)} - {formatTime(dayHours.close)}
          </div>
        );
      });
    }
  };

  const PickupIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 19H3M19 19H17M3 19H17M3 19V7.32837M17 19V7.32837M3 7.32837C2.96299 7.307 2.92651 7.28434 2.8906 7.2604L2.35024 6.90016C1.56331 6.37554 1.25144 5.37141 1.60269 4.49328L2.49711 2.25722C2.80084 1.4979 3.53626 1 4.35407 1H15.6459C16.4637 1 17.1992 1.4979 17.5029 2.25722L18.3973 4.49328C18.7486 5.37141 18.4367 6.37554 17.6498 6.90016L17.1094 7.2604C17.0735 7.28434 17.037 7.307 17 7.32837M3 7.32837C3.65537 7.70699 4.47351 7.68433 5.1094 7.2604L7 6L8.8906 7.2604C9.5624 7.70827 10.4376 7.70827 11.1094 7.2604L13 6L14.8906 7.2604C15.5265 7.68433 16.3446 7.70699 17 7.32837" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 19V14C12 12.8954 11.1046 12 10 12V12C8.89543 12 8 12.8954 8 14V19" stroke="white" strokeWidth="2"/>
    </svg>
  );

  const DeliveryIcon = () => (
    <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1V0C0.447715 0 0 0.447715 0 1L1 1ZM12 1H13C13 0.447715 12.5523 0 12 0V1ZM12 7V6C11.7348 6 11.4804 6.10536 11.2929 6.29289C11.1054 6.48043 11 6.73478 11 7H12ZM1 1V2H12V1V0H1V1ZM12 1H11V17H12H13V1H12ZM1 15H2V1H1H0V15H1ZM12 7V8H17V7V6H12V7ZM21 11H20V15H21H22V11H21ZM12 17H13L13 7H12H11L11 17H12ZM18.4142 18.4142L17.7071 17.7071C17.3166 18.0976 16.6834 18.0976 16.2929 17.7071L15.5858 18.4142L14.8787 19.1213C16.0503 20.2929 17.9497 20.2929 19.1213 19.1213L18.4142 18.4142ZM15.5858 15.5858L16.2929 16.2929C16.6834 15.9024 17.3166 15.9024 17.7071 16.2929L18.4142 15.5858L19.1213 14.8787C17.9497 13.7071 16.0503 13.7071 14.8787 14.8787L15.5858 15.5858ZM6.41421 18.4142L5.70711 17.7071C5.31658 18.0976 4.68342 18.0976 4.29289 17.7071L3.58579 18.4142L2.87868 19.1213C4.05025 20.2929 5.94975 20.2929 7.12132 19.1213L6.41421 18.4142ZM3.58579 15.5858L4.29289 16.2929C4.68342 15.9024 5.31658 15.9024 5.70711 16.2929L6.41421 15.5858L7.12132 14.8787C5.94975 13.7071 4.05025 13.7071 2.87868 14.8787L3.58579 15.5858ZM18.4142 15.5858L17.7071 16.2929C17.9026 16.4884 18 16.7425 18 17H19H20C20 16.2338 19.7069 15.4643 19.1213 14.8787L18.4142 15.5858ZM19 17H18C18 17.2575 17.9026 17.5116 17.7071 17.7071L18.4142 18.4142L19.1213 19.1213C19.7069 18.5357 20 17.7662 20 17H19ZM15 17V16H12V17V18H15V17ZM15.5858 18.4142L16.2929 17.7071C16.0974 17.5116 16 17.2575 16 17H15H14C14 17.7662 14.2931 18.5357 14.8787 19.1213L15.5858 18.4142ZM15 17H16C16 16.7425 16.0974 16.4884 16.2929 16.2929L15.5858 15.5858L14.8787 14.8787C14.2931 15.4643 14 16.2338 14 17H15ZM3.58579 18.4142L4.29289 17.7071C4.09744 17.5116 4 17.2575 4 17H3H2C2 17.7662 2.29309 18.5357 2.87868 19.1213L3.58579 18.4142ZM3 17H4C4 16.7425 4.09744 16.4884 4.29289 16.2929L3.58579 15.5858L2.87868 14.8787C2.29309 15.4643 2 16.2338 2 17H3ZM12 17V16H7V17V18H12V17ZM6.41421 15.5858L5.70711 16.2929C5.90256 16.4884 6 16.7425 6 17H7H8C8 16.2338 7.70691 15.4643 7.12132 14.8787L6.41421 15.5858ZM7 17H6C6 17.2575 5.90256 17.5116 5.70711 17.7071L6.41421 18.4142L7.12132 19.1213C7.70691 18.5357 8 17.7662 8 17H7ZM21 15H20C20 15.5523 19.5523 16 19 16V17V18C20.6569 18 22 16.6569 22 15H21ZM17 7V8C18.6569 8 20 9.34315 20 11H21H22C22 8.23858 19.7614 6 17 6V7ZM1 15H0C0 16.6569 1.34315 18 3 18V17V16C2.44772 16 2 15.5523 2 15H1Z" fill="white"/>
        <path d="M1 6H4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1 10H6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CateringIcon = () => (
    <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 9.6L18.3086 10.2568C17.5652 10.9631 16.4081 10.9926 15.6295 10.3253L14.8016 9.61564C14.0526 8.97366 12.9474 8.97366 12.1984 9.61564L11.3016 10.3844C10.5526 11.0263 9.4474 11.0263 8.69842 10.3844L7.80158 9.61564C7.0526 8.97366 5.9474 8.97366 5.19842 9.61563L4.37045 10.3253C3.5919 10.9926 2.4348 10.963 1.69138 10.2568L1 9.6M10 1C4.73593 1 2.5508 2.71052 1.64374 4.13061C1.04915 5.06149 1.89543 6 3 6H17C18.1046 6 18.9508 5.06149 18.3563 4.13061C17.4492 2.71052 15.2641 1 10 1ZM2.5 14H17.5C18.3284 14 19 14.6716 19 15.5V15.5C19 16.3284 18.3284 17 17.5 17H2.5C1.67157 17 1 16.3284 1 15.5V15.5C1 14.6716 1.67157 14 2.5 14Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const MapIcon = () => (
    <svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.79883 7.92285C1.79883 12.7747 6.0433 16.7869 7.92202 18.3252C8.19089 18.5454 8.32694 18.6568 8.52754 18.7132C8.68374 18.7572 8.91362 18.7572 9.06982 18.7132C9.2708 18.6567 9.40589 18.5463 9.67578 18.3254C11.5545 16.7871 15.7987 12.7751 15.7987 7.9233C15.7987 6.08718 15.0613 4.32605 13.7485 3.02772C12.4358 1.72939 10.6554 1 8.79891 1C6.94239 1 5.16184 1.7295 3.84908 3.02783C2.53633 4.32616 1.79883 6.08674 1.79883 7.92285Z" stroke="#2E2E2E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.79883 7C6.79883 8.10457 7.69426 9 8.79883 9C9.9034 9 10.7988 8.10457 10.7988 7C10.7988 5.89543 9.9034 5 8.79883 5C7.69426 5 6.79883 5.89543 6.79883 7Z" stroke="#2E2E2E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const PhoneIcon = () => (
    <svg width="16" height="21" viewBox="0 0 16 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.75825 18.7963C4.66841 15.942 1.52042 10.4895 1.59346 4.65444C1.6211 2.44548 3.87121 1.16556 6.00507 1.73733L6.971 1.99615C7.50446 2.13909 7.81568 2.68714 7.72606 3.23211C7.57464 4.15283 7.55557 5.07035 7.6564 5.96309L5.79567 7.03739C6.29835 9.73029 7.71488 12.1838 9.79566 13.9656L11.6564 12.8913C12.3791 13.425 13.1833 13.8672 14.0563 14.1965C14.5731 14.3913 14.8921 14.9349 14.7492 15.4683L14.4904 16.4343C13.9186 18.5681 11.6851 19.8768 9.75825 18.7963Z" stroke="#2E2E2E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ClockIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 4.55556V9H13.4444M9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9C17 13.4183 13.4183 17 9 17Z" stroke="#2E2E2E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CalendarIcon = () => (
    <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.75 2.88889H4.00018C2.95008 2.88889 2.42464 2.88889 2.02356 3.09477C1.67076 3.27586 1.38413 3.56461 1.20436 3.92003C1 4.32409 1 4.85342 1 5.9113V6.66667M4.75 2.88889H12.25M4.75 2.88889V1M12.25 2.88889H13.0002C14.0503 2.88889 14.5746 2.88889 14.9757 3.09477C15.3285 3.27586 15.6161 3.56461 15.7958 3.92003C16 4.32369 16 4.85238 16 5.90819V6.66667M12.25 2.88889V1M1 6.66667V14.978C1 16.0358 1 16.5645 1.20436 16.9686C1.38413 17.324 1.67076 17.6132 2.02356 17.7943C2.42425 18 2.94906 18 3.9971 18H13.0029C14.0509 18 14.575 18 14.9757 17.7943C15.3285 17.6132 15.6161 17.324 15.7958 16.9686C16 16.5649 16 16.037 16 14.9812V6.66667M1 6.66667H16M12.25 14.2222H12.2519L12.2518 14.2241L12.25 14.2241V14.2222ZM8.5 14.2222H8.50187L8.50183 14.2241L8.5 14.2241V14.2222ZM4.75 14.2222H4.75187L4.75183 14.2241L4.75 14.2241V14.2222ZM12.2518 10.4444V10.4463L12.25 10.4463V10.4444H12.2518ZM8.5 10.4444H8.50187L8.50183 10.4463L8.5 10.4463V10.4444ZM4.75 10.4444H4.75187L4.75183 10.4463L4.75 10.4463V10.4444Z" stroke="#2E2E2E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className={`column ${className}`}>
      <div className="columns">
        {/* Left Column - Content */}
        <div className="column is-half">
          <h1 className="location colorBlack">Location</h1>
          <h2 className="location colorBlack">{locationData.name}</h2>
          
          {/* Address */}
          <div className="my-4">
            <div className="is-flex is-flex-direction-row is-align-content-center">
                <div className="mr-3 my-0 p-0">
                    <MapIcon />
                </div>
                <p className="mr-3 my-0 p-0">
                    {locationData.address.address1} {locationData.address.address2}<br />
                    {locationData.address.city}, {locationData.address.state} {locationData.address.zip}<br />
                    {locationData.googleDirectionsUrl && (
                    <a href={locationData.googleDirectionsUrl} target="_blank" rel="noopener noreferrer">
                    Map & Directions
                    </a>
                )}
                </p>
            </div>
          </div>

          {/* Phone */}
          <div className="my-5">
            <div className="is-flex is-flex-direction-row is-align-content-center">
                <div className="mr-3 my-0 p-0">
                    <PhoneIcon />
                </div>
            
                <p className="mr-3 my-0 p-0">
                    <a href={`tel:${locationData.phoneNumber}`}>
                        {locationData.phoneNumber}
                    </a>
                </p>
            </div>
          </div>

          {/* Hours */}
          <div className="my-5">
            <div className="is-flex is-flex-direction-row is-align-content-center">
                <div className="mr-3 my-0 p-0">
                    <ClockIcon />
                </div>
            
                <div className="mr-3 my-0 p-0">
                    {formatHours(locationData.hours)}
                </div>
            </div>
          </div>

          {/* Current Status */}
          {locationData.currentStatus && (
            <div className="my-5">
                <div className="is-flex is-flex-direction-row is-align-items-center">
                    <div className="mr-3 my-0 p-0">
                        <CalendarIcon />
                    </div>
                
                    <p className={`${locationData.currentStatus.isOpen ? 'colorBlack' : 'colorBlack'}`}>
                        {locationData.currentStatus.message}
                    </p>
                </div>
            </div>
          )}

          {/* Order Buttons */}
          <div className="pt-4 mb-4">
            <div className="is-flex is-flex-wrap-wrap" style={{gap: '12px'}}>
              <div 
                className="locationOrdering"
                role="button"
                tabIndex="0"
                onClick={() => {
                  handlePickupClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePickupClick();
                  }
                }}
                style={{
                  width: '9rem',
                  height: '5.375rem',
                  borderRadius: '0.25rem',
                  backgroundColor: 'var(--red-color)',
                  border: '3px solid var(--red-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 'bold',
                  userSelect: 'none'
                }}
              >
                <p className="mt-1">
                    <PickupIcon /> 
                </p>
                <p className="mt-0 small is-bold colorWhite">Order Pickup</p>
              </div>
              
              <div 
                className="locationOrdering"
                role="button"
                tabIndex="0"
                onClick={() => {
                  handleDeliveryClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleDeliveryClick();
                  }
                }}
                style={{
                  width: '9rem',
                  height: '5.375rem',
                  borderRadius: '0.25rem',
                  backgroundColor: 'var(--red-color)',
                  border: '3px solid var(--red-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 'bold',
                  userSelect: 'none'
                }}
              >
                <p className="mt-1">
                    <DeliveryIcon />
                </p>
                <p className="mt-0 small is-bold colorWhite">Order Delivery</p>
              </div>
              
              <div 
                className="locationOrdering"
                role="button"
                tabIndex="0"
                onClick={() => {
                  handleCateringClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCateringClick();
                  }
                }}
                style={{
                  width: '9rem',
                  height: '5.375rem',
                  borderRadius: '0.25rem',
                  backgroundColor: 'var(--red-color)',
                  border: '3px solid var(--red-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 'bold',
                  userSelect: 'none'
                }}
              >
                <p className="mt-1">
                    <CateringIcon />
                </p>
                <p className="mt-0 small is-bold colorWhite">Request Catering</p>
              </div>
            </div>
          </div>

          {/* Active Announcement */}
          {/* {locationData.hasActiveAnnouncement && locationData.announcement && (
            <div className="backgroundColorYellow">
              <p className="small">{locationData.announcement.text}</p>
            </div>
          )} */}
        </div>

        {/* Right Column - Image */}
        <div className="column is-half">
          {locationData.photoSrc && (
            <figure className="image">
              <img 
                className="br"
                src={`/images/locations/${locationData.photoSrc}`}
                alt={`${locationData.name} location`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  minHeight: '400px'
                }}
              />
            </figure>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationPageContent;